import { IMapCoordinates, IMapGeoObject } from '@cian/frontend-newbuilding-map-component';
import * as React from 'react';
import { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNewbuildingInfrastructureAction } from 'shared/actions/newbuildingInfrastructure';
import { transformFutureRoads } from 'shared/mappers/transformFutureRoads';
import { transformTransportAccessibilityInfrastructure } from 'shared/mappers/transformTransportAccessibilityInfrastructure';
import { selectGeoCoordinates } from 'shared/selectors';
import { getNewbuildingId, selectInfrastructureTypes } from 'shared/selectors/newObject';
import { getIsMapServicesEnabled, selectFutureMapVisible } from 'shared/selectors/newbuildingMap';
import {
  IInfrastructureData,
  IInfrastructureType,
  ITAInfrastructure,
  ITAInfrastructureData,
  TFutureRoad,
  TInfrastructureItem,
  TRegularInfrastructureType,
  TTAInfrastructureType,
} from 'shared/types/map';
import { getGeoObject } from 'shared/utils/map';

import { getInfrastructureItems, getInfrastructureMapByYear, hasFutureObjects } from '../utils';
import { toGeoObjects } from '../utils/mappers';
import { getTransportAccessibilityInfrastructureMapByType } from '../utils/transportAccessibility';

export const useGeoObjects = () => {
  const pinCoordinates = useSelector(selectGeoCoordinates);
  const newbuildingId = useSelector(getNewbuildingId);
  const actualTypes = useSelector(selectInfrastructureTypes);
  const isFutureMapVisible = useSelector(selectFutureMapVisible);
  const isMapServicesEnabled = useSelector(getIsMapServicesEnabled);
  const [infrastructureTypes, setInfrastructureTypes] = React.useState<IInfrastructureType[] | null>(null);
  const infrastructureByYearRef = useRef(new Map<number, IInfrastructureData>());
  const transportAccessibilityInfrastructureByTypeRef = useRef(new Map<TTAInfrastructureType, ITAInfrastructureData>());

  const [isInfrastructureFetched, setInfrastructureFetched] = useState(false);

  const dispatch = useDispatch();

  const setActualInfrastructureTypes = (infrastructureItems: TInfrastructureItem[]) => {
    if (!actualTypes) {
      return;
    }

    const infrastructureItemTypes = new Set<TRegularInfrastructureType | TFutureRoad>(
      infrastructureItems.map(item => item.type),
    );
    const existedTypes = actualTypes.filter(({ type }) => infrastructureItemTypes.has(type));
    setInfrastructureTypes(existedTypes.length ? existedTypes : null);
  };

  const handleGeoObjectsFirstFetch = async (): Promise<IMapGeoObject[]> => {
    const isFetched =
      infrastructureByYearRef.current.size || transportAccessibilityInfrastructureByTypeRef.current.size;

    if (!isFetched) {
      const mainObjects = [];

      const main = getGeoObject({
        name: 'main',
        coordinates: pinCoordinates as IMapCoordinates,
        id: '#newbuilding',
      });

      mainObjects.push(main);

      let infrastructure: TInfrastructureItem[];
      let transportAccessibilityInfrastructure: ITAInfrastructure | undefined;

      if (actualTypes) {
        if (isFutureMapVisible) {
          const response = await getInfrastructureItems(newbuildingId as number);

          const existingInfrastructure = response.infrastructure;
          const futureInfrastructure = response.futureInfrastructure || [];
          const futureRoads = transformFutureRoads(response.futureRoads);

          infrastructure = [...existingInfrastructure, ...futureInfrastructure, ...futureRoads];
          transportAccessibilityInfrastructure = transformTransportAccessibilityInfrastructure(
            response.transportAccessibility,
          );
        } else {
          const { infrastructure: existingInfrastructure, transportAccessibility } = await getInfrastructureItems(
            newbuildingId as number,
          );

          infrastructure = existingInfrastructure;
          transportAccessibilityInfrastructure = transformTransportAccessibilityInfrastructure(transportAccessibility);
        }
      } else {
        infrastructure = [];
      }

      dispatch(
        setNewbuildingInfrastructureAction({
          regularInfrastructureItems: infrastructure,
          transportAccessibilityInfrastructure,
        }),
      );

      const isFutureInfrastructureServiceVisible = isMapServicesEnabled && hasFutureObjects(infrastructure);

      const transportAccessibilityInfrastructureMapByType = getTransportAccessibilityInfrastructureMapByType(
        main,
        null,
        transportAccessibilityInfrastructure,
      );
      transportAccessibilityInfrastructureByTypeRef.current = transportAccessibilityInfrastructureMapByType;

      const infrastructureMapByYear = getInfrastructureMapByYear(
        infrastructure,
        mainObjects,
        isFutureInfrastructureServiceVisible,
      );
      infrastructureByYearRef.current = infrastructureMapByYear;

      const geoObjects = toGeoObjects(infrastructureMapByYear, new Date().getFullYear());

      setActualInfrastructureTypes(infrastructure);
      setInfrastructureFetched(true);

      return Promise.resolve(geoObjects);
    }

    return Promise.resolve([]);
  };

  return {
    isInfrastructureFetched,
    infrastructureByYearRef,
    infrastructureTypes,
    onGeoObjectsFirstFetch: handleGeoObjectsFirstFetch,
    transportAccessibilityInfrastructureByTypeRef,
  };
};
